/**
 * Container interaction panel (M3.3a C4).
 *
 * Opened when the player clicks on a container prop. Shows:
 * - Container contents as a PropStack list (same format as inventory)
 * - Weight bar (current_weight / capacity_weight)
 * - Access level indicator
 * - Access denial feedback if player lacks permission
 *
 * Drag-drop between inventory and container emits Deposit/Withdraw
 * PropTransfer intents to the server. Full drag-drop UX ships later;
 * for now, click-to-transfer provides the interaction.
 */

import { X } from 'lucide-react';

import type { AccessLevel } from '../wire';
import { useInventory } from './store';
import { StackItem } from './StackItem';
import { WeightBar } from './WeightBar';
import styles from './inventory.module.css';

function accessBadgeClass(access: AccessLevel): string {
  switch (access) {
    case 'Public':
      return styles.accessPublic ?? '';
    case 'OwnerOnly':
      return styles.accessOwner ?? '';
    case 'CoetusMembers':
      return styles.accessCoetus ?? '';
    default:
      return styles.accessOwner ?? '';
  }
}

function accessLabel(access: AccessLevel): string {
  switch (access) {
    case 'Public':
      return 'Public';
    case 'OwnerOnly':
      return 'Owner Only';
    case 'CoetusMembers':
      return 'Coetus';
    case 'Friends':
      return 'Friends';
    default:
      return String(access);
  }
}

export function ContainerPanel(): JSX.Element | null {
  const container = useInventory((s) => s.openContainer);
  const accessDenied = useInventory((s) => s.accessDenied);
  const close = useInventory((s) => s.closeContainerPanel);

  if (!container) return null;

  return (
    <div
      className={styles.containerPanel}
      role="region"
      aria-label="Container"
      data-testid="container-panel"
    >
      <header className={styles.header}>
        <h3 className={styles.title}>Container</h3>
        <span className={`${styles.accessBadge} ${accessBadgeClass(container.access)}`} data-testid="access-indicator">
          {accessLabel(container.access)}
        </span>
        <button
          type="button"
          className={styles.closeButton}
          onClick={close}
          aria-label="Close container"
        >
          <X size={16} />
        </button>
      </header>
      <div className={styles.body}>
        {accessDenied ? (
          <div className={styles.accessDenied}>
            Access denied. You do not have permission to open this container.
          </div>
        ) : (
          <>
            <WeightBar
              current={container.current_weight}
              max={container.capacity_weight}
              label="Capacity"
            />
            {container.stacks.length === 0 ? (
              <div className={styles.emptyState}>Container is empty</div>
            ) : (
              <ul className={styles.stackList}>
                {container.stacks.map((stack, i) => (
                  <StackItem
                    key={`${stack.prop_id}-${stack.quality}-${i}`}
                    stack={stack}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
